import { put, all, call, select, takeEvery, take, delay } from 'redux-saga/effects'
import FontFaceObserver from 'fontfaceobserver'
import bowser from 'bowser'
import {
  SET_LOADING, LOAD_ASSETS, ON_INIT, UPDATE_PROGRESS, HIDE_LOADER, SET_MUTE,
  assetTypes, ON_LOGIN
} from '../constants'
import {
  setMute, setSoundAvailable, loadSounds, soundsLoaded,
} from '../actions'
import { initialize } from '../../Sounds'
import { getFromLocalStorage, getParameterByName } from '../../Utils'

/**
 * Object containing information about the loading progress of different assets.
 */
const loadingProgress = { }

// we need to store this for when the sounds are loaded
let assetList

/**
 * Creates a list of sagas that should be run on specific events.
 */
export default function* watchLoadData() {
  yield takeEvery(LOAD_ASSETS, loadAssets)
  yield takeEvery(SET_MUTE, onSetMute)
}

/**
 * Updates the loadingProgress object with new values
 * @param {string} key - The name of the loading type (e.g. assets, sounds..).
 * @param {value} value - The new progress of the type.
 */
function updateProgress(key, value) {
  loadingProgress[key] = value
}

/**
 * Saga that loops and updates the loading bar.
 */
function* progressLoop() {
  let progress = 0
  while (progress < 100) {
    const { base, session } = yield select()

    const tmpAssets = { ...base.assetProgress, ...loadingProgress }

    const keys = Object.keys(tmpAssets)
    const keyLength = keys.length
    progress = 0
    const keysToUpdate = {}
    let keyIndex = keyLength

    while (keyIndex--) {
      const key = keys[keyIndex]
      const baseProgress = base.assetProgress[key] || 0
      const tmpProgress = loadingProgress[key] || 0

      if (baseProgress < tmpProgress) {
        keysToUpdate[key] = tmpProgress
        yield put({
          type: UPDATE_PROGRESS,
          progress: tmpProgress,
          asset: key
        })
      }

      progress += (Math.max(baseProgress, tmpProgress) / keyLength)
    }

    if (!session.developerMode && !session.loadedLocale) {
      progress -= 1
    }

    yield delay(10)
  }
}

/**
 * Saga that will start the loading process for assets.
 * @param {object} action - Object containing information about the assets that
 * should be loaded.
 */
function* loadAssets(action) {
  yield put({
    type: SET_LOADING,
    loading: true
  })

  HidePrePreLoader()

  const { Assets } = action
  assetList = Assets

  const fonts = Assets.filter(asset => asset.type === assetTypes.font)
  const sounds = Assets.filter(asset => asset.type === assetTypes.sound)
  const callbacks = Assets.filter(asset => !!asset.onLoad).map(asset => asset.onLoad)

  const fontPromises = []

  let fontCount = 0
  if (fonts.length > 0) {
    updateProgress('fonts', 0)
    fonts.forEach(font => {
      const src = `@font-face {
        font-family: "${font.id}";
        src: url("${font.src}") format("truetype");
      }`
      const node = document.createElement('style')
      node.innerHTML = src
      document.body.appendChild(node)

      const fontTimeout = 5000
      const fontLoader = new FontFaceObserver(font.id)
      const fontP = fontLoader.load(null, fontTimeout)
      .finally(() => { updateProgress('fonts', (++fontCount / fonts.length) * 100) })
      .catch(() => {
        console.log(`Font loading (${fontTimeout}ms) timed out for ${font.id}`)
      })
      fontPromises.push(fontP)
    })
  }

  const isSoundAvailable = !getParameterByName('disableSound') && !bowser.msie && sounds.length > 0

  yield put(setSoundAvailable(isSoundAvailable))

  let soundPromise = null
  if (isSoundAvailable && getFromLocalStorage('muted') !== 'true' && !bowser.mobile && !bowser.tablet) {
    soundPromise = initialize(sounds, updateProgress)
  }

  yield all([ call(progressLoop), soundPromise, ...fontPromises ])

  if (soundPromise) {
    yield put(soundsLoaded())
    yield put(setMute(false))
  }

  yield put({ type: ON_INIT })

  /**
   * An array of callbacks that should be iterated through,
   * once all assets have loaded.
   */
  callbacks.forEach(cb => {
    cb()
  })

  const sessionId = yield select(state => state.session.sid)
  if (!sessionId) {
    yield take(ON_LOGIN)
  }

  yield put({
    type: SET_LOADING,
    loading: false
  })

  yield delay(500)

  yield put({
    type: HIDE_LOADER
  })
}

/**
 * Saga that will load sounds assets when sound is toggled on, if
 * they haven't been loaded yet.
 * @param {object} action - Object containing information about if sounds have been toggled on or off.
 */
export function* onSetMute(action) {
  const { mute } = action
  const { soundAvailable, loadingSounds, soundsLoaded: soundsHaveLoaded } = yield select(state => state.base)

  if (!mute && soundAvailable && !soundsHaveLoaded && !loadingSounds) {
    const sounds = assetList.filter(asset => asset.type === assetTypes.sound)

    yield put(loadSounds())

    if (sounds.length > 0) {
      yield initialize(sounds, updateProgress)
    }

    yield put(soundsLoaded())
  }
}

let prePreLoaderHidden = false

export function HidePrePreLoader() {
  if (prePreLoaderHidden) {
    return
  }

  prePreLoaderHidden = true

  if (window.usesNewPreloader) {
    window.onPrePreLoaderDone = () => {
      window.prePreLoaderIsDone = true
      const prePreLoader = document.getElementById('PrePreLoader')
      if (!!prePreLoader) {
        prePreLoader.className += ' fadeOut'
        setTimeout(() => {
          prePreLoader.parentNode.removeChild(prePreLoader)
        }, 1500)
      }
    }


    if (window.prePreLoaderIsDone) {
      window.onPrePreLoaderDone()
    }
  } else {
    const prePreLoader = document.getElementById('PrePreLoader')
    if (!!prePreLoader) {
      prePreLoader.parentNode.removeChild(prePreLoader)
    }
  }
}
